import { useEffect, useMemo, useRef, useState } from 'react'
import { useMachine } from '@xstate/react'
import { StepDefinition } from '../types'
import { buildMachine } from '../machines/createFlowMachine'
import { useRootStore } from 'stores/RootStore'

export function useFlow<C = any>({
  id,
  steps,
  fetchInitialData,
  onComplete,
  states = {},
}: {
  id: string
  steps: StepDefinition<C>[]
  fetchInitialData: () => Promise<Record<string, any>>
  onComplete?: () => void
  states?: any
}) {
  /* build machine only once per id/steps */
  const machine = useMemo(() => buildMachine(id, steps, states), [id, steps, states])
  const [current, send] = useMachine(machine)
  const { featureFlags } = useRootStore()

  /* bootstrap */
  const [booting, setBoot] = useState(true)
  useEffect(() => {
    if (!featureFlags.ready) return
    fetchInitialData().then((payload) => {
      send({ type: 'DATA_READY', payload })
      setBoot(false)
    })
  }, [fetchInitialData, send, featureFlags.ready])

  /* fire completion once */
  const doneRef = useRef(false)
  useEffect(() => {
    if (!current.matches('done') || doneRef.current) return
    doneRef.current = true
    onComplete?.()
  }, [current, onComplete])

  return {
    current,
    send,
    isLoading: booting || current.matches('loading'),
    isDone: current.matches('done'),
  }
}
